import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { allTinyMCEPlugins } from '@adornis/writer/x-tinymce.js';
import { Adresse } from '@chancenwerk/db/adresse.js';
import { CampusNotificationFilter } from '@chancenwerk/db/filter/CampusNotificationFilter.js';
import { CampusNotification } from '@chancenwerk/db/notifications/CampusNotification.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-image.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import '@chancenwerk/design/components/cr-tabs.js';
import '@chancenwerk/design/components/fonts.js';
import { School } from '@chancenwerk/master-data-management/partner/entities/School.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { filter, of, switchMap } from 'rxjs';
import '../components/campus-component-section-lesson-list.js';
import { CampusDialogEditNotification } from '../components/dialogs/campus-dialog-edit-notification.js';
import { CampusDialogSendNotification } from '../components/dialogs/campus-dialog-send-notification.js';
import { campusTinyPluginJumpMarks } from '../components/tinyPlugins/campus-tiny-plugin-jumpmarks.js';

const partnerSelector: BaseQLSelectionSet<School> = {
  _id: 1,
  _class: 1,
  name: 1,
  kuerzel: 1,
  address: Adresse.allFields,
};

/**
 * @customElement campus-page-administration-notifications
 */
@customElement('campus-page-administration-notifications')
export class CampusPageAdministrationNotifications extends CrPage {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  @state() private readonly _notification = new RXController(this, new CampusNotification({}));

  constructor() {
    super();
    this.permission = ChancenPermission.global.EDIT_NOTIFICATIONS;
    this.label = 'Neuigkeiten';
    this.description = 'Hier kannst du neue Neuigkeiten erstellen und an ausgewählte Personengruppen schicken';
  }

  @state() private readonly _hasPermissionForGlobalScope = new RXController(
    this,
    currentUser<ChancenwerkUser>().pipe(
      filter(Boolean),
      switchMap(user =>
        !user ? of(false) : of(user.hasPermission(ChancenPermission.global.EDIT_NOTIFICATIONS, GLOBAL_CONTEXT)),
      ),
    ),
  );

  @state() private readonly _partners = new RXController(this, School.subscribeAll<School>()(partnerSelector));

  private readonly _columnDefinitions: Array<ColumnDefinition<typeof CampusNotification>> = [
    {
      columnName: 'angelegt am',
      fieldName: 'createdAt',
      getFieldMapping(entityInstance) {
        return entityInstance.createdAt?.toFormat('dd.LL.yyyy');
      },
      columnWidth: '150px',
    },
    {
      columnName: 'Titel',
      fieldName: 'title',
      getFieldMapping(entityInstance) {
        return entityInstance.title;
      },
    },
    {
      columnName: 'Bearbeitung',
      customID: 'editing',
      renderItem(entityInstance) {
        return html`
          <cr-flex horizontal space="sm" croassaxis-center center>
            <cr-button
              ${css({ color: '#747474' })}
              mode="icon"
              icon="settings"
              @click=${async () => {
                await CampusDialogEditNotification.showPopup({
                  props: {
                    notification: entityInstance,
                  },
                });
              }}
            ></cr-button>
          </cr-flex>
        `;
      },
      columnWidth: '150px',
    },
  ];

  private readonly _form = new FormController(this, this._notification.observable, {
    success: async notification => {
      notification.createdByID = this._currentUser.value?._id;
      const sent = await CampusDialogSendNotification.showPopup({
        draggable: this.size.mobile,
        modal: true,
        props: {
          notification,
        },
      });
      if (sent) {
        this._notification.next(new CampusNotification({}));
      }
    },
  });

  override styles() {
    return [
      ...super.styles(),
      {
        '.send-card': {
          background: this.colors.primary.shade(300),
          transition: 'all 0.25s ease-in-out',
          cursor: 'pointer',
        },
        '.send-card:hover': {
          background: this.colors.primary,
        },
      },
    ];
  }

  override content() {
    return html`
      <cr-flex flex crossaxis-center space="sm" padding="xs">
        <cr-card space="sm">
          <cr-input placeholder="Überschrift" ${this._form.field('title')}></cr-input>
          <x-tinymce
            ${this._form.field('content')}
            menubar="false"
            editable
            minheight="50vh"
            placeholder="Inhalt"
            .editorOptions=${{
              convert_urls: false,
              plugins: [...allTinyMCEPlugins, 'campus-tiny-plugin-jumpmarks'],
              toolbar:
                'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent lineheight | numlist bullist | forecolor backcolor removeformat | charmap emoticons | fullscreen | campus-tiny-plugin-jumpmarks insertfile image media template link anchor codesample | ltr rtl | table tablemergecells',
            }}
            .customPlugins=${[campusTinyPluginJumpMarks]}
            .value=${this._notification.value.content}
          ></x-tinymce>
        </cr-card>
        <cr-card w100 flex center crossaxis-center class="send-card" @click=${() => this._form.validate()}>
          <cr-tooltip><cr-text>Neuigkeit versenden</cr-text></cr-tooltip>
          <cr-icon ${css({ color: 'white', fontSize: '2rem' })}>send</cr-icon>
        </cr-card>
      </cr-flex>

      <x-data-view
        .options=${{
          class: CampusNotification,
          view: new View({
            filter: new CampusNotificationFilter({
              noTimeLimit: true,
              forSchoolIDs: this._hasPermissionForGlobalScope.value ? [] : this._partners.value?.map(p => p._id),
              onlyOwnSchool: !this._hasPermissionForGlobalScope.value,
            }),
          }),
          operation: CampusNotification.subscribeNotificationsByFilterPaginated,
          selectionSet: CampusNotification.allFields,
          onlyShowLastTranslation: true,
          customColumns: this._columnDefinitions,
          isEntryClickable: false,
        } satisfies DataViewOptions<typeof CampusNotification>}
      >
      </x-data-view>
    `;
  }
}
